import { createAsyncThunk } from "@reduxjs/toolkit";
import { alertVariants } from "@visiblethread/react-components";
import localhost from "apis/localhost";
import { extractLockedByUser } from "apis/utils";
import { expandRow, getPathToTableRow } from "components/ExcelTable";
import { findIndex, get, has, last } from "lodash";
import { showErrorModal } from "slices/app/actions/error";
import {
  addToJobQueue,
  disableNavBarAndSideBar,
  removeFromJobQueue,
  showGlobalAlert,
} from "slices/app/appSlice";
import { selectUsername } from "slices/app/selectors";
import getNextAvailableTitle from "utils/getNextAvailableTitle";

import { ERROR_TITLE, NEW_LIST_PREFIX } from "../constants";
import { selectWatchwordsList } from "../list/selectors";
import {
  addChildByPath,
  setDataAsLocked,
  setIsDirty,
  setIsEditing,
} from "./editWatchwordsSlice";
import {
  selectEditWatchwordsData,
  selectWatchwordsListCategories,
  selectWatchwordsListDescription,
  selectWatchwordsListName,
  selectWatchwordsListTypes,
} from "./selectors";
import {
  createEmptyTerm,
  createEmptyWatchwordList,
  getRequestBody,
} from "./utils";

export const fetchWatchwordsListById = createAsyncThunk(
  "watchwords/edit/fetchWatchwordsListById",
  async (listId, { dispatch, getState }) => {
    dispatch(addToJobQueue("fetchWatchwordsById"));
    const state = getState();
    const currentUser = selectUsername(state);

    try {
      const rsp = await localhost.get(`watchwords/${listId}`);
      const data = rsp?.data;
      return { currentUser, data };
    } catch (err) {
      dispatch(showErrorModal(err, ERROR_TITLE));
      return null;
    } finally {
      dispatch(removeFromJobQueue("fetchWatchwordsById"));
    }
  }
);

export const parseLockingError = (error, dispatch) => {
  if (error.status === 423) {
    const lockedBy = extractLockedByUser(error);
    dispatch(setDataAsLocked({ lockedBy, lockedByAnotherUser: true }));
  } else {
    dispatch(showErrorModal(error, ERROR_TITLE));
  }
};

export const lockForEditing = createAsyncThunk(
  "watchwords/edit/lockForEditing",
  async (listId, { dispatch }) => {
    dispatch(addToJobQueue("lockForEditing"));

    try {
      await localhost.post(`watchwords/lock/${listId}`);

      dispatch(setIsEditing(true));
      dispatch(disableNavBarAndSideBar(true));
    } catch (error) {
      parseLockingError(error, dispatch);
    } finally {
      dispatch(removeFromJobQueue("lockForEditing"));
    }
  }
);

export const unlockToRelease = createAsyncThunk(
  "watchwords/edit/unlockToRelease",
  async (listId, { dispatch }) => {
    dispatch(addToJobQueue("unlockToRelease"));

    try {
      await localhost.post(`watchwords/unlock/${listId}`);

      dispatch(setIsDirty(false));
      dispatch(setIsEditing(false));
      dispatch(disableNavBarAndSideBar(false));
    } catch (err) {
      parseLockingError(err, dispatch);
    } finally {
      dispatch(removeFromJobQueue("unlockToRelease"));
      dispatch(fetchWatchwordsListById(listId));
    }
  }
);

export const setEmptyWatchwordList = createAsyncThunk(
  "watchwords/edit/new",
  (createdBy, { dispatch, getState }) => {
    const state = getState();
    const list = selectWatchwordsList(state);
    const name = getNextAvailableTitle(NEW_LIST_PREFIX, list);
    const newList = createEmptyWatchwordList({ createdBy, name });

    dispatch(disableNavBarAndSideBar(true));

    return newList;
  }
);

export const addEmptyTermByRow = (row) => (dispatch, getState) => {
  const state = getState();
  const categories = selectWatchwordsListCategories(state);
  const isCategoryRow = has(row.original, "terms");

  const term = createEmptyTerm();

  // If the Row is a category, then we just need to add a blank
  // Term to the first index of the Category's array of Terms.
  if (isCategoryRow) {
    const categoryId = row.original.id;
    const categoryIdx = findIndex(categories, ["id", categoryId]);

    dispatch(
      addChildByPath({
        pathToChild: [categoryIdx, "terms"],
        child: term,
        index: 0,
      })
    );

    expandRow(row);

    return term;
  }

  // Get the full path to this particular row (ie, Term);
  const pathToEdit = getPathToTableRow(row, { subRowKey: "terms" });

  // Then figure out the index from which we append a new Term
  // Plus the path to the terms[] for the specific category.
  const index = last(pathToEdit);
  const pathToChild = pathToEdit.slice(0, -1);

  const pathToCategory = pathToChild.slice(0, -1);
  const category = get(categories, pathToCategory);

  term.categoryId = category?.id;

  dispatch(addChildByPath({ pathToChild, child: term, index: index + 1 }));

  return term;
};

const getIsNameBlank = (m) =>
  m.field === "name" && m.error === "must not be blank";

const getHasInvalidChars = (m) =>
  (m.error || "").includes(
    "There are invalid character(s) in your Watchword list name"
  );

const mapType = ({ id, typeName }) => ({ id, typeName });

export const cloneWatchwordsList = createAsyncThunk(
  "watchwords/edit/clone",
  async (list, { dispatch }) => {
    const { name, description } = list;
    const types = (list.types || []).map(mapType);
    const categories = getRequestBody(list.categories, true);

    const body = { categories, description, name, types };
    dispatch(addToJobQueue("cloneWatchwordsList"));
    try {
      return await localhost.post("watchwords", body);
    } catch (err) {
      dispatch(showErrorModal(err));
      return false;
    } finally {
      dispatch(removeFromJobQueue("cloneWatchwordsList"));
    }
  }
);

export const saveWatchwordsList = createAsyncThunk(
  "watchwords/edit/save",
  async (_, { dispatch, getState }) => {
    dispatch(addToJobQueue("saveWatchwordsList"));
    try {
      const state = getState();
      const name = selectWatchwordsListName(state);
      const description = selectWatchwordsListDescription(state);
      const stateTypes = selectWatchwordsListTypes(state);
      const types = stateTypes.map(mapType);
      const data = selectEditWatchwordsData(state);
      const isNewList = Boolean(data.isNew);

      const categories = getRequestBody(data.categories);
      const body = { categories, description, name, types };

      const action = isNewList ? "post" : "put";
      const url = isNewList ? "watchwords" : `watchwords/${data.id}`;
      const rsp = await localhost[action](url, body);

      return rsp.data;
    } catch (err) {
      const { data } = err.response || {};

      const messages = Array.isArray(data) ? data : [data];
      const nameBlank = messages.find(getIsNameBlank);
      const invalidCharacters = messages.find(getHasInvalidChars);

      if (nameBlank || invalidCharacters) {
        dispatch(
          showGlobalAlert({
            variant: alertVariants.danger,
            text:
              invalidCharacters?.error || "Watchword List name cannot be blank",
          })
        );
      } else {
        dispatch(showErrorModal(err));
      }
    } finally {
      dispatch(removeFromJobQueue("saveWatchwordsList"));
      dispatch(disableNavBarAndSideBar(false));
    }
  }
);
